import { useRequest } from "ahooks";
import cn from "classnames";
import dayjs from "dayjs";
import { meanBy } from "lodash";

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Chip,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import {
  ApiGetFlightRoutesSampleParams,
  ApiRoutesItem,
  ApiRoutesItemPoints,
  getFlightRoutesCalendar,
  postRoutesRefresh,
} from "@/api/routes";
import MessageDialog from "@/components/MessageDialog";
import ProChip from "@/components/ProChip";
import { CabinsColors } from "@/constants/config";
import { FREE_PANORAMA_RANGE, FREE_PANORAMA_SEARCH_DAYS } from "@/constants/membership";
import { REFRESH_PROGRAM_NAME_MAP, VOTE_PROGRAM_NAME_MAP } from "@/constants/programs";
import { PROGRAM_NAME_MAP } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useCabinsColorIdentify from "@/hooks/useCabinsColorIdentify";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import CabinsColorIdentify from "@/pages/Routes/components/CabinsColorIdentify";
import FlightDetailsModal from "@/pages/Routes/components/FlightDetails";
import Sort from "@/pages/Routes/components/Sort";
import { sortFn } from "@/pages/Routes/components/Sort/sortConfig";
import { CabinsType } from "@/typings";

import Calendar from "./Calendar";
import RouteCharts from "./Charts";
import List from "./List";
import styles from "./index.module.css";

const PointGrid = () => {
  const theme = useThemeData();
  const [mode, setMode] = useState<"calendar" | "list" | string>("list");

  const { isMobile } = usePlatform();
  const [params] = useSearchParams();
  const route = params.get("route");
  const defaultProgram = params.get("program");
  const [programs, setPrograms] = useState<string[]>([]);
  const [program, setProgram] = useState("");

  const [isLoading, setLoading] = useState(true);

  const [routesData, setRoutesData] = useState<ApiRoutesItem[]>([]);

  const [sortKey, setSortKey] = useState<number>(-1);
  const [listData, setListData] = useState<ApiRoutesItem[]>([]);
  const [listFilterData, setListFilterData] = useState<ApiRoutesItem[]>([]);

  const [result, setResult] = useState<{ message: string; status: 0 | 1 }>();

  const [currentParams, setCurrentParams] = useState<
    ApiGetFlightRoutesSampleParams & { type: CabinsType }
  >();
  const [open, setOpen] = useState(false);

  const { cabinsColorIdentifyVisible, hideColorIdentify } = useCabinsColorIdentify();

  const handleModeChange = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value);
  });
  const store = useGlobalStoreContext();
  const show_upsell = store?.show_upsell || false;

  const { runAsync, loading: isRefresh } = useRequest(postRoutesRefresh, { manual: true });

  const showDays = useMemo(
    () =>
      [...new Array(store?.user_type === 0 ? 60 : 365)].map((_, i) =>
        dayjs().add(i, "day").format("YYYY-MM-DD")
      ),
    [store?.user_type]
  );

  const lastRefreshValue = useMemo(() => {
    const average = meanBy(listFilterData.map((item) => item.points.ls));
    return average ? dayjs(average * 1000).fromNow() : "few days ago";
  }, [listFilterData]);

  const SwitchBar = useMemo(() => {
    return (
      <div className="!mb-[15px] flex items-center">
        <FormControl className="!mb-[0]">
          <RadioGroup row name="mode-radio-buttons-group" value={mode} onChange={handleModeChange}>
            <FormControlLabel
              value="calendar"
              control={<Radio />}
              label={<Typography color="textPrimary">Calendar View</Typography>}
            />
            <FormControlLabel
              value="list"
              control={<Radio />}
              label={<Typography color="textPrimary">List View</Typography>}
            />
          </RadioGroup>
        </FormControl>
        {!isMobile && <span>Last Refresh: {lastRefreshValue}</span>}
      </div>
    );
  }, [handleModeChange, isMobile, lastRefreshValue, mode]);

  const loadData = useCallback(async () => {
    try {
      if (route) {
        const res = await getFlightRoutesCalendar({ id: route });

        const filterData = (res.data || []).filter((ele) => ele.route === route);
        const programs = [...new Set(filterData.map((ele) => ele.program))].filter(
          (ele) => ele !== defaultProgram
        );

        if (defaultProgram) {
          programs.unshift(defaultProgram);
        }

        setProgram(programs[0]);
        setPrograms(programs);
        setRoutesData(filterData as ApiRoutesItem[]);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }, [route, defaultProgram]);

  const getCalendarItemData = useCallback(
    (date: string) => {
      const data = routesData
        .filter((ele) => ele.program === program)
        .find((ele) => ele.date === date);

      const points = data?.points || ({} as ApiRoutesItemPoints);
      const fontSize = isMobile ? 10 : 16;
      return {
        date,
        showDate: showDays.length ? showDays.includes(date) : true,
        points: [
          {
            value: points.y,
            color: CabinsColors.economy,
            unit: "pts",
            type: "Economy",
            date,
            icon:
              data?.points_ns?.y && data?.points_ns?.y == data?.points?.y ? (
                <StarBorderIcon sx={{ fontSize }} />
              ) : null,
          },
          {
            value: points.w,
            color: CabinsColors.premiumEconomy,
            unit: "pts",
            type: "Premium Economy",
            date,
            icon:
              data?.points_ns?.w && data?.points_ns?.w == data?.points?.w ? (
                <StarBorderIcon sx={{ fontSize }} />
              ) : null,
          },
          {
            value: points.j,
            color: CabinsColors.business,
            unit: "pts",
            type: "Business",
            date,
            icon:
              data?.points_ns?.j && data?.points_ns?.j == data?.points?.j ? (
                <StarBorderIcon sx={{ fontSize }} />
              ) : null,
          },
          {
            value: points.f,
            color: CabinsColors.first,
            unit: "pts",
            type: "First",
            date,
            icon:
              data?.points_ns?.f && data?.points_ns?.f == data?.points?.f ? (
                <StarBorderIcon sx={{ fontSize }} />
              ) : null,
          },
        ],
      };
    },
    [routesData, program, isMobile, showDays]
  );

  const calendarItemClick = useEvent((v) => {
    const data = routesData.find((ele) => ele.date === v.date);

    if (data) {
      setCurrentParams({
        program: program,
        origin: data.route.split("-")[0],
        arrival: data.route.split("-")[1],
        date: dayjs(data.date).format("YYYYMMDD"),
        type: v.type,
      });

      setOpen(true);
    }
  });

  const listItemClick = useEvent((v) => {
    setCurrentParams({
      program: v.program,
      origin: v.route.split("-")[0],
      arrival: v.route.split("-")[1],
      date: dayjs(v.date).format("YYYYMMDD"),
      type: v.type,
    });

    setOpen(true);
  });

  const sortChange = useEvent((sortKey: number) => {
    setSortKey(sortKey);
  });

  useLayoutEffect(() => {
    const sortFilterData = sortFn(listData, sortKey);
    setListFilterData(sortFilterData);
  }, [listData, sortKey]);

  useLayoutEffect(() => {
    const data = routesData.filter((ele) => ele.program === program);

    setListData(data);
    setSortKey(-1);
  }, [routesData, program]);

  useEffect(() => {
    setSortKey(-1);
  }, [mode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if the map contains the program as key
  const programExists = REFRESH_PROGRAM_NAME_MAP.has(program);

  return (
    <div>
      {isLoading ? (
        <div className="my-[16px]">
          <LinearProgress />
        </div>
      ) : (
        <Paper elevation={4}>
          <div className="px-[10px]">
            {cabinsColorIdentifyVisible && (
              <div className="pt-[15px] pb-[0px] overflow-x-auto no-scrollbar">
                <CabinsColorIdentify onClose={hideColorIdentify} />
              </div>
            )}

            <div className="w-full overflow-hidden">
              <div
                className={cn("flex py-[10px] overflow-x-auto", styles.noScrollbar)}
                style={{ flexWrap: isMobile ? "nowrap" : "wrap" }}
              >
                {programs.map((ele) => {
                  return (
                    <Chip
                      key={ele}
                      className="!my-[5px] !mr-[10px]"
                      color="primary"
                      label={PROGRAM_NAME_MAP.get(ele)}
                      variant={program === ele ? "filled" : "outlined"}
                      onClick={() => setProgram(ele)}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-[10px] flex justify-between items-center">
            <div>{SwitchBar}</div>

            {programExists && (
              <LoadingButton
                variant="contained"
                size="small"
                sx={{ mb: "15px" }}
                loading={isRefresh}
                startIcon={isMobile ? null : <SentimentVerySatisfiedIcon />}
                onClick={async () => {
                  try {
                    if (route && program) {
                      const data = await runAsync({
                        origin: route?.split("-")[0],
                        arrival: route?.split("-")[1],
                        program: program,
                      });
                      setResult({
                        status: 1,
                        message: data.message,
                      });
                    }
                  } catch (error: any) {
                    setResult({
                      status: 0,
                      message: error.message,
                    });
                  }
                }}
              >
                {isMobile ? "refresh" : "Request Refresh"}
              </LoadingButton>
            )}
          </div>
          {isMobile && <div className="mt-[-15px] pl-[12px]">
            <span>Last Refresh: {lastRefreshValue}</span>
          </div>}
          {show_upsell ? (
            <Typography
              style={{
                paddingBottom: isMobile ? "16px" : "16px",
                paddingTop: isMobile ? 0 : 0,
                fontSize: isMobile ? "14px" : "16px",
                maxWidth: isMobile ? "45 0px" : "625px",
              }}
              className="py-[6px] px-[12px] break-words"
            >
              <Typography>
                {"Free users can access only " + FREE_PANORAMA_RANGE + " days of data."}
              </Typography>
              <Typography fontWeight={"bold"} color="primary">
                Upgrade to <ProChip nomargin /> and unlock access to 365 days of data.
              </Typography>
            </Typography>
          ) : null}

          {mode === "list" && (
            <RouteCharts
              data={listFilterData}
              onItemClick={(date) => {
                calendarItemClick({ date: date });
              }}
            />
          )}

          <div className="px-[10px]">
            {mode === "calendar" ? (
              <Calendar getItemData={getCalendarItemData} onItemClick={calendarItemClick} />
            ) : listFilterData.length === 0 && !isLoading ? (
              <div className="py-[80px] text-center">
                <Typography color="textSecondary">
                  No data was sourced for this route program.
                </Typography>
                <Typography color="textSecondary">
                  Please request refresh or come back later.
                </Typography>

                <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
              </div>
            ) : (
              <>
                <div className="flex items-center px-[5px] mb-[10px]">
                  <Typography variant="body1" color="textPrimary">
                    {`${listData.length}` + " results"}
                  </Typography>
                  <div className="flex-1" />
                  <Sort value={sortKey} onChange={sortChange} />
                </div>
                <List data={listFilterData} onClick={listItemClick} />
              </>
            )}
          </div>
        </Paper>
      )}

      {open && (
        <FlightDetailsModal open={open} onClose={() => setOpen(false)} params={currentParams} />
      )}

      <MessageDialog
        open={!!result}
        onOpen={() => setResult(undefined)}
        message={result?.message}
        status={result?.status}
      />
    </div>
  );
};

export default React.memo(PointGrid);
